import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Camera, CheckCircle, ImageSquare, MagnifyingGlass, PencilSimple, X, ArrowUp, ArrowDown, Plus, ChatCircleText, ArrowSquareOut, Check, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import PhotoLightbox from "@/components/PhotoLightbox";
import SuggestPhotoDialog from "@/components/SuggestPhotoDialog";
import { uploadPhoto } from "@/lib/uploadPhoto";
import CoverPhotoField from "@/components/products/CoverPhotoField";

const MAX_PHOTOS = 10;
const MAX_BYTES = 8 * 1024 * 1024;  // 8 MB original (resized down to ~250 KB)

// A product is considered "with photos" if it has at least one image.
const photoList = (p) => {
  if (Array.isArray(p?.photos)) return p.photos.filter(Boolean);
  return p?.photos_url ? [p.photos_url] : [];
};
const hasPhoto = (p) => photoList(p).length > 0 || !!(p?.cover_photo || "").trim();

export default function Marketing() {
  const [products, setProducts] = useState([]);
  const [tab, setTab] = useState("missing"); // "missing" | "done" | "suggestions"
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [draftPhotos, setDraftPhotos] = useState([]); // array of data-URLs
  const [draftCover, setDraftCover] = useState(""); // foto de portada (va primero en la tienda)
  const [saving, setSaving] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestFor, setSuggestFor] = useState(null);
  // lightbox state: { open, photos[], title, initialIndex, filenameBase }
  const [lb, setLb] = useState({ open: false, photos: [], title: "", initialIndex: 0, filenameBase: "" });

  const load = async () => {
    const { data } = await api.get("/products");
    setProducts(data);
  };
  const loadSuggestions = async () => {
    try {
      const { data } = await api.get("/photo-suggestions?status=pending");
      setSuggestions(Array.isArray(data) ? data : []);
    } catch { setSuggestions([]); }
  };
  useEffect(() => { load(); loadSuggestions(); }, []);

  // `missing` = productos que necesitan foto **y** están disponibles (stock > 0).
  // Los que ya se vendieron (stock 0) sin foto no se muestran; volverán a
  // aparecer si el inventario recibe stock nuevo.
  const counts = useMemo(() => ({
    missing: products.filter((p) => !hasPhoto(p) && (p.stock || 0) > 0).length,
    done: products.filter(hasPhoto).length,
  }), [products]);

  const filtered = useMemo(() => {
    const t = search.trim().toLowerCase();
    return products
      .filter((p) => (tab === "missing" ? (!hasPhoto(p) && (p.stock || 0) > 0) : hasPhoto(p)))
      .filter((p) => !t || (p.name || "").toLowerCase().includes(t) || (p.brand || "").toLowerCase().includes(t) || (p.sku || "").toLowerCase().includes(t))
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [products, tab, search]);

  const openEditor = (p) => {
    setEditing(p);
    setDraftPhotos(photoList(p));
    setDraftCover(p.cover_photo || "");
  };
  const closeEditor = () => { setEditing(null); setDraftPhotos([]); setDraftCover(""); };

  const addFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    const remaining = MAX_PHOTOS - draftPhotos.length;
    if (remaining <= 0) { toast.error(`Máximo ${MAX_PHOTOS} fotos por producto`); return; }
    const toProcess = files.slice(0, remaining);
    if (files.length > remaining) toast.warning(`Se procesarán ${remaining} fotos (límite ${MAX_PHOTOS})`);

    const processing = toast.loading(`Procesando ${toProcess.length} foto${toProcess.length === 1 ? "" : "s"}…`);
    const added = [];
    for (const file of toProcess) {
      if (file.size > MAX_BYTES) { toast.error(`"${file.name}" excede 8 MB`); continue; }
      if (!/^image\/(png|jpe?g|webp|heic|heif)$/i.test(file.type)) {
        toast.error(`"${file.name}" no es PNG/JPG/WEBP`);
        continue;
      }
      try {
        const result = await uploadPhoto(file, {
          folder: "alendrapc/marketing",
          maxSide: 1600,
          quality: 0.85,
        });
        added.push(result);
      } catch (err) {
        toast.error(err?.message || `No pude subir "${file.name}"`);
        console.warn(err);
      }
    }
    toast.dismiss(processing);
    if (added.length > 0) {
      setDraftPhotos((prev) => [...prev, ...added]);
      toast.success(`${added.length} foto${added.length === 1 ? "" : "s"} agregada${added.length === 1 ? "" : "s"}`);
    }
  };

  const removePhoto = (idx) => setDraftPhotos((prev) => prev.filter((_, i) => i !== idx));
  const movePhoto = (idx, dir) => setDraftPhotos((prev) => {
    const j = idx + dir;
    if (j < 0 || j >= prev.length) return prev;
    const next = [...prev];
    [next[idx], next[j]] = [next[j], next[idx]];
    return next;
  });

  const save = async () => {
    if (draftPhotos.length === 0 && !draftCover.trim()) {
      toast.error("Sube al menos una foto");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.patch(`/products/${editing.id}/photos`, {
        photos: draftPhotos,
        cover_photo: draftCover.trim(),
      });
      setProducts((prev) => prev.map((p) => (p.id === editing.id ? { ...p, photos: data.photos, photos_url: data.photos_url, cover_photo: data.cover_photo || null } : p)));
      // Auto-resolve all pending suggestions for this product (marketing
      // just uploaded new photos in response to them).
      const pending = suggestions.filter((s) => s.product_id === editing.id && s.status === "pending");
      if (pending.length > 0) {
        await Promise.all(pending.map((s) => api.patch(`/photo-suggestions/${s.id}`, { status: "resolved" }).catch(() => {})));
        await loadSuggestions();
        toast.success(`"${editing.name}" actualizado · ${pending.length} sugerencia${pending.length === 1 ? "" : "s"} marcada${pending.length === 1 ? "" : "s"} como resuelta${pending.length === 1 ? "" : "s"}`);
      } else {
        toast.success(`"${editing.name}" actualizado · ${draftPhotos.length} foto${draftPhotos.length === 1 ? "" : "s"}`);
      }
      closeEditor();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al guardar");
    } finally { setSaving(false); }
  };

  const openLightbox = (p, initialIndex = 0) => {
    const photos = photoList(p);
    if (photos.length === 0) return;
    setLb({
      open: true,
      photos,
      title: p.name,
      initialIndex,
      filenameBase: p.name,
    });
  };

  return (
    <div className="p-6 sm:p-8 space-y-6" data-testid="marketing-page">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-4xl font-bold tracking-tight">Marketing · Fotos</h1>
          <p className="text-sm text-zinc-500 mt-1">Sube hasta {MAX_PHOTOS} fotos por producto. Se redimensionan automáticamente para que carguen rápido sin perder calidad.</p>
        </div>
        <div className="relative w-full sm:w-72">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input placeholder="Buscar nombre, marca o SKU…" value={search} onChange={(e)=>setSearch(e.target.value)} className="pl-9" data-testid="mkt-search" />
        </div>
      </header>

      <div className="flex gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => setTab("missing")}
          data-testid="mkt-tab-missing"
          className={`flex items-center gap-2 px-4 py-2.5 rounded-sm border text-sm font-semibold transition ${tab === "missing" ? "bg-[#E0000F]/10 border-[#E0000F]/40 text-[#E0000F]" : "bg-white border-zinc-200 text-zinc-600 hover:bg-zinc-50"}`}
        >
          <Camera size={16} weight="bold"/>
          Sin fotos <span className="text-[10px] font-normal opacity-70">(con stock)</span>
          <span className="font-mono-data text-xs px-2 py-0.5 bg-white rounded">{counts.missing}</span>
        </button>
        <button
          type="button"
          onClick={() => setTab("done")}
          data-testid="mkt-tab-done"
          className={`flex items-center gap-2 px-4 py-2.5 rounded-sm border text-sm font-semibold transition ${tab === "done" ? "bg-[#00873D]/10 border-[#00873D]/40 text-[#00873D]" : "bg-white border-zinc-200 text-zinc-600 hover:bg-zinc-50"}`}
        >
          <CheckCircle size={16} weight="bold"/>
          Con fotos
          <span className="font-mono-data text-xs px-2 py-0.5 bg-white rounded">{counts.done}</span>
        </button>
        <button
          type="button"
          onClick={() => setTab("suggestions")}
          data-testid="mkt-tab-suggestions"
          className={`flex items-center gap-2 px-4 py-2.5 rounded-sm border text-sm font-semibold transition ${tab === "suggestions" ? "bg-amber-100 border-amber-400 text-amber-800" : "bg-white border-zinc-200 text-zinc-600 hover:bg-zinc-50"}`}
        >
          <ChatCircleText size={16} weight="bold"/>
          Sugerencias de cambio
          <span className={`font-mono-data text-xs px-2 py-0.5 rounded ${suggestions.length > 0 ? "bg-amber-200 text-amber-900" : "bg-white"}`}>{suggestions.length}</span>
        </button>
      </div>

      {tab === "suggestions" ? (
        <SuggestionsList
          suggestions={suggestions}
          onOpenProduct={(pid) => {
            const p = products.find((x) => x.id === pid);
            if (p) openEditor(p);
          }}
          onChange={loadSuggestions}
        />
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-zinc-300 rounded p-10 text-center text-zinc-400 dotted-bg">
          {tab === "missing" ? "¡Todos los productos ya tienen foto! 🎉" : "Aún no hay productos con foto subida."}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filtered.map((p) => {
            const photos = photoList(p);
            const cover = (p.cover_photo || "").trim() || photos[0];
            return (
              <article key={p.id} className="border border-zinc-200 bg-white p-3 flex flex-col gap-2 hover:shadow-md transition" data-testid={`mkt-card-${p.id}`}>
                <button
                  type="button"
                  onClick={() => openLightbox(p, 0)}
                  className="aspect-video bg-zinc-50 border border-zinc-100 rounded-sm overflow-hidden flex items-center justify-center relative group"
                  data-testid={`mkt-card-thumb-${p.id}`}
                  aria-label={`Ver fotos de ${p.name}`}
                >
                  {cover ? (
                    <>
                      <img src={cover} alt={p.name} className="max-w-full max-h-full object-contain transition-transform group-hover:scale-[1.02]" />
                      {photos.length > 1 && (
                        <span className="absolute bottom-1 right-1 px-1.5 py-0.5 text-[10px] font-mono-data bg-black/70 text-white rounded">
                          +{photos.length - 1}
                        </span>
                      )}
                    </>
                  ) : (
                    <ImageSquare size={32} className="text-zinc-300" />
                  )}
                </button>
                <div className="flex-1">
                  <h3 className="font-semibold text-sm leading-tight" title={p.name}>{p.name}</h3>
                  <div className="text-[11px] text-zinc-500 mt-0.5">
                    {[p.brand, p.category, p.sku].filter(Boolean).join(" · ") || "—"}
                  </div>
                  {p.characteristics && (
                    <p className="text-[11px] text-zinc-600 mt-1 line-clamp-2" title={p.characteristics}>{p.characteristics}</p>
                  )}
                </div>
                <div className="flex flex-col sm:flex-row gap-1.5 shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openEditor(p)}
                    className="gap-1 text-xs"
                    data-testid={`mkt-edit-${p.id}`}
                  >
                    {hasPhoto(p) ? <><PencilSimple size={13}/> Editar fotos</> : <><Camera size={13}/> Subir fotos</>}
                  </Button>
                  {hasPhoto(p) && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setSuggestFor(p)}
                      className="gap-1 text-xs border-amber-300 text-amber-700 hover:bg-amber-50"
                      data-testid={`mkt-suggest-${p.id}`}
                      title="Marcar esta foto como NO conforme y enviar feedback al equipo de Marketing"
                    >
                      <ChatCircleText size={13}/> Sugerir cambio
                    </Button>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}

      {/* Editor modal */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={closeEditor}>
          <div className="bg-white rounded-md max-w-xl w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto" onClick={(e)=>e.stopPropagation()} data-testid="mkt-editor">
            <div>
              <h2 className="font-heading text-2xl font-bold">{editing.name}</h2>
              <p className="text-xs text-zinc-500">{[editing.brand, editing.category, editing.sku].filter(Boolean).join(" · ") || "—"}</p>
            </div>

            <CoverPhotoField value={draftCover} gallery={draftPhotos} onChange={setDraftCover} />

            <div>
              <Label className="text-xs uppercase tracking-wider">
                Galería ({draftPhotos.length}/{MAX_PHOTOS}) · la primera es la foto principal · se redimensionan automáticamente
              </Label>

              {/* Photo grid */}
              {draftPhotos.length > 0 && (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-2" data-testid="mkt-photo-grid">
                  {draftPhotos.map((src, i) => (
                    <div key={`${i}-${src.slice(-32)}`} className="relative group border border-zinc-200 rounded overflow-hidden aspect-square bg-zinc-50">
                      <img src={src} alt={`Foto ${i + 1}`} className="w-full h-full object-contain" />
                      {i === 0 && (
                        <span className="absolute top-1 left-1 px-1.5 py-0.5 text-[9px] font-bold bg-zinc-800 text-white rounded">PRINCIPAL</span>
                      )}
                      <button
                        type="button"
                        onClick={() => removePhoto(i)}
                        className="absolute top-1 right-1 p-1 bg-black/60 text-white rounded hover:bg-[#E0000F]"
                        aria-label="Quitar"
                        data-testid={`mkt-remove-${i}`}
                      >
                        <X size={12} weight="bold"/>
                      </button>
                      <div className="absolute bottom-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition">
                        <button type="button" onClick={() => movePhoto(i, -1)} disabled={i === 0} className="p-1 bg-black/60 text-white rounded disabled:opacity-30" aria-label="Mover arriba" data-testid={`mkt-up-${i}`}>
                          <ArrowUp size={12} weight="bold"/>
                        </button>
                        <button type="button" onClick={() => movePhoto(i, 1)} disabled={i === draftPhotos.length - 1} className="p-1 bg-black/60 text-white rounded disabled:opacity-30" aria-label="Mover abajo" data-testid={`mkt-down-${i}`}>
                          <ArrowDown size={12} weight="bold"/>
                        </button>
                      </div>
                    </div>
                  ))}
                  {draftPhotos.length < MAX_PHOTOS && (
                    <label className="aspect-square border-2 border-dashed border-zinc-300 rounded flex flex-col items-center justify-center text-xs text-zinc-500 hover:bg-zinc-50 cursor-pointer">
                      <Plus size={18}/>
                      Agregar
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        multiple
                        className="hidden"
                        onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
                        data-testid="mkt-file-input-add"
                      />
                    </label>
                  )}
                </div>
              )}

              {draftPhotos.length === 0 && (
                <label className="mt-2 block border-2 border-dashed border-zinc-300 rounded p-6 text-center text-xs text-zinc-500 hover:bg-zinc-50 cursor-pointer">
                  <Camera size={28} className="mx-auto text-zinc-400 mb-1"/>
                  Click para seleccionar varias fotos (PNG/JPG/WEBP)
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    multiple
                    className="hidden"
                    onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
                    data-testid="mkt-file-input"
                  />
                </label>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={closeEditor} disabled={saving}>Cancelar</Button>
              <Button type="button" onClick={save} disabled={saving} className="bg-[#002FA7] hover:bg-[#002277] text-white" data-testid="mkt-save">
                {saving ? "Guardando…" : "Guardar"}
              </Button>
            </div>
          </div>
        </div>
      )}

      <PhotoLightbox
        open={lb.open}
        onClose={() => setLb((s) => ({ ...s, open: false }))}
        photos={lb.photos}
        initialIndex={lb.initialIndex}
        title={lb.title}
        filenameBase={lb.filenameBase}
      />
      <SuggestPhotoDialog
        product={suggestFor}
        onClose={() => setSuggestFor(null)}
        onSubmitted={() => { loadSuggestions(); }}
      />
    </div>
  );
}


/**
 * Lista de sugerencias pendientes para Marketing. Cada tarjeta muestra el
 * producto, las sugerencias recibidas y un botón para abrir el editor de
 * fotos (al guardar nuevas fotos las sugerencias se marcan resueltas
 * automáticamente). Marketing también puede descartar manualmente.
 */
function SuggestionsList({ suggestions, onOpenProduct, onChange }) {
  // Group suggestions by product
  const grouped = useMemo(() => {
    const map = new Map();
    for (const s of suggestions) {
      if (!map.has(s.product_id)) map.set(s.product_id, { product: s.product || { name: s.product_name, id: s.product_id }, items: [] });
      map.get(s.product_id).items.push(s);
    }
    return [...map.values()];
  }, [suggestions]);

  const dismiss = async (sid) => {
    try {
      await api.patch(`/photo-suggestions/${sid}`, { status: "dismissed" });
      toast.success("Sugerencia descartada");
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al descartar");
    }
  };
  const resolve = async (sid) => {
    try {
      await api.patch(`/photo-suggestions/${sid}`, { status: "resolved" });
      toast.success("Sugerencia marcada como resuelta");
      onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al resolver");
    }
  };

  if (grouped.length === 0) {
    return (
      <div className="border border-dashed border-zinc-300 rounded p-10 text-center text-zinc-400 dotted-bg" data-testid="no-suggestions">
        <ChatCircleText size={32} weight="duotone" className="mx-auto text-zinc-300 mb-2" />
        Sin sugerencias pendientes. ¡Buen trabajo! 🎉
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="suggestions-list">
      {grouped.map(({ product, items }) => {
        const cover = (product?.photos || [])[0];
        return (
          <div key={product.id} className="border border-amber-200 bg-amber-50/30 rounded-md p-4" data-testid={`suggestion-card-${product.id}`}>
            <div className="flex items-center gap-3 flex-wrap">
              <button type="button" onClick={() => onOpenProduct(product.id)} className="flex items-center gap-3 hover:opacity-80 transition-opacity flex-1 min-w-0 text-left">
                {cover ? (
                  <img src={cover} alt="" className="w-16 h-16 object-cover rounded border border-zinc-200" />
                ) : (
                  <div className="w-16 h-16 bg-zinc-100 rounded border border-zinc-200 flex items-center justify-center"><ImageSquare size={24} className="text-zinc-300" /></div>
                )}
                <div className="min-w-0">
                  <div className="font-semibold truncate">{product.name}</div>
                  <div className="text-xs text-zinc-500 truncate">{product.brand}{product.sku ? ` · SKU ${product.sku}` : ""}</div>
                  <div className="text-[11px] text-amber-700 mt-0.5 font-semibold">{items.length} sugerencia{items.length === 1 ? "" : "s"} pendiente{items.length === 1 ? "" : "s"}</div>
                </div>
              </button>
              <Button onClick={() => onOpenProduct(product.id)} className="bg-amber-600 hover:bg-amber-700 text-white gap-1" data-testid={`fix-photos-${product.id}`}>
                <Camera size={14} weight="bold" /> Subir fotos
              </Button>
            </div>
            <div className="mt-3 space-y-2">
              {items.map((s) => (
                <div key={s.id} className="bg-white border border-amber-200 rounded p-3 text-sm" data-testid={`suggestion-item-${s.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] text-zinc-500 uppercase tracking-wider font-mono">
                        {s.created_by_name || s.created_by_email} · {new Date(s.created_at).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" })}
                      </div>
                      <p className="mt-1 text-zinc-800 whitespace-pre-line">{s.message}</p>
                      {s.reference_url && (
                        <a href={s.reference_url} target="_blank" rel="noopener noreferrer" className="text-[#002FA7] hover:underline inline-flex items-center gap-1 mt-1.5 text-xs">
                          <ArrowSquareOut size={12} /> Ver referencia
                        </a>
                      )}
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <button onClick={() => resolve(s.id)} className="p-1.5 hover:bg-emerald-100 text-emerald-700 rounded" title="Marcar resuelta" data-testid={`resolve-${s.id}`}>
                        <Check size={14} weight="bold" />
                      </button>
                      <button onClick={() => dismiss(s.id)} className="p-1.5 hover:bg-zinc-100 text-zinc-500 rounded" title="Descartar" data-testid={`dismiss-${s.id}`}>
                        <Trash size={14} weight="bold" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
